import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#3B5EDF";
const LIME = "#D9F154";
const INK = "#141414";
const BG = "#FAF7EF";

const pageStyle = {
  background: BG,
  color: INK,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 24px",
  fontFamily: "'Archivo', sans-serif",
  textAlign: "center",
};

const monoStyle = {
  fontFamily: "'Archivo', sans-serif",
  fontSize: "11px",
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  fontWeight: 700,
};

const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900&family=Archivo+Black&display=swap');`;

const chunkyBtn = {
  ...monoStyle,
  background: LIME,
  color: INK,
  textDecoration: "none",
  border: `2px solid ${INK}`,
  borderRadius: 14,
  boxShadow: `4px 4px 0 ${INK}`,
  padding: "16px 30px",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-block",
};

export function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (provider === "paypal") {
      setStatus("paid");
      return;
    }
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data: resp } = await axios.get(`${API}/payments/status/${sessionId}`);
        setData(resp);
        if (resp.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (resp.payment_status === "expired" || resp.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= 12) {
          setStatus("failed");
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch {
        if (attemptsRef.current >= 12) {
          setStatus("failed");
        } else {
          timerRef.current = setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId, provider]);

  return (
    <div style={pageStyle} data-testid="success-page">
      <style>{`${fontImport} body { background: ${BG}; margin: 0; }`}</style>
      <div style={{ marginBottom: 36 }}>
        <LogoMark size={56} />
      </div>

      {status === "checking" && (
        <>
          <div style={{ ...monoStyle, color: BLUE, marginBottom: 18 }} data-testid="success-checking">Confirmando tu pago</div>
          <div style={{ fontSize: 56, fontWeight: 900, letterSpacing: "-0.03em" }}>···</div>
          <p style={{ marginTop: 18, opacity: 0.6, fontSize: 14 }}>Esto tarda unos segundos.</p>
        </>
      )}

      {status === "paid" && (
        <>
          <div style={{ ...monoStyle, color: BLUE, marginBottom: 18 }}>— Confirmado</div>
          <h1 style={{ fontSize: "clamp(40px,6vw,64px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.02, maxWidth: 760 }} data-testid="success-title">
            ¡Listo! Tu compra <span style={{ color: BLUE }}>quedó confirmada.</span>
          </h1>
          <p style={{ marginTop: 22, opacity: 0.78, fontSize: 17, maxWidth: 560, lineHeight: 1.6 }}>
            Te mandé un correo con el acceso y los detalles en los próximos minutos. Revisa tu bandeja (y el spam, por si las dudas).
          </p>
          {data && data.product_name && (
            <div style={{ marginTop: 36, padding: "22px 30px", border: `2px solid ${INK}`, borderRadius: 16, boxShadow: `4px 4px 0 ${INK}`, background: "#FFFFFF", maxWidth: 460, width: "100%" }}>
              <div style={{ ...monoStyle, opacity: 0.5, marginBottom: 10, fontSize: 9 }}>Recibo</div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}>
                <span>{data.product_name}</span>
                {data.amount != null && (
                  <span style={{ color: BLUE }}>${Number(data.amount).toFixed(2)} {(data.currency || "usd").toUpperCase()}</span>
                )}
              </div>
            </div>
          )}
          {data && Array.isArray(data.downloads) && data.downloads.length > 0 && (
            <div style={{ marginTop: 20, maxWidth: 460, width: "100%" }} data-testid="success-downloads">
              <div style={{ ...monoStyle, opacity: 0.5, marginBottom: 10, fontSize: 9 }}>Tu material</div>
              {data.downloads.map((d, i) => (
                <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" data-testid={`success-download-${i}`}
                  style={{ ...chunkyBtn, display: "block", marginTop: 10, background: BLUE, color: "#fff" }}>
                  Descargar: {d.title} →
                </a>
              ))}
            </div>
          )}
          {data && data.gift && (
            <div style={{ marginTop: 20, padding: "22px 26px", border: `2px solid ${INK}`, borderRadius: 16, boxShadow: `4px 4px 0 ${INK}`, background: LIME, maxWidth: 460, width: "100%" }} data-testid="success-gift">
              <div style={{ ...monoStyle, marginBottom: 8, fontSize: 9 }}>De regalo</div>
              <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>Un ebook de otra vertical, cortesía de la casa.</div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>{data.gift.title}</div>
              <a href={data.gift.url} target="_blank" rel="noopener noreferrer" data-testid="success-gift-download"
                style={{ ...chunkyBtn, display: "inline-block", marginTop: 14, background: "#FFFFFF" }}>
                Descargar mi regalo →
              </a>
            </div>
          )}
          <Link to="/" style={{ ...chunkyBtn, marginTop: 44 }} data-testid="success-home-link">
            ← Volver al inicio
          </Link>
        </>
      )}

      {(status === "failed" || status === "expired") && (
        <>
          <div style={{ ...monoStyle, color: BLUE, marginBottom: 18 }}>Estado</div>
          <h1 style={{ fontSize: "clamp(34px,5vw,52px)", fontWeight: 900, letterSpacing: "-0.03em" }} data-testid="success-failed-title">
            No pudimos confirmar el pago.
          </h1>
          <p style={{ marginTop: 18, opacity: 0.7, maxWidth: 520, fontSize: 15, lineHeight: 1.6 }}>
            Puede que tu sesión haya expirado. Si el cargo sí pasó, revisa tu correo — si no, inténtalo de nuevo.
          </p>
          <Link to="/" style={{ ...chunkyBtn, marginTop: 36 }}>
            ← Volver al inicio
          </Link>
        </>
      )}
    </div>
  );
}

export function Cancel() {
  const navigate = useNavigate();
  return (
    <div style={pageStyle} data-testid="cancel-page">
      <style>{`${fontImport} body { background: ${BG}; margin: 0; }`}</style>
      <div style={{ marginBottom: 36 }}>
        <LogoMark size={56} />
      </div>
      <div style={{ ...monoStyle, color: BLUE, marginBottom: 18 }}>— Cancelado</div>
      <h1 style={{ fontSize: "clamp(38px,5.5vw,56px)", fontWeight: 900, letterSpacing: "-0.03em" }} data-testid="cancel-title">
        No se hizo ningún cargo.
      </h1>
      <p style={{ marginTop: 18, opacity: 0.7, maxWidth: 520, fontSize: 15, lineHeight: 1.6 }}>
        Cuando quieras retomar, aquí estará. Los lugares son limitados.
      </p>
      <button onClick={() => navigate("/")} data-testid="cancel-home-btn" style={{ ...chunkyBtn, marginTop: 36 }}>
        ← Volver al inicio
      </button>
    </div>
  );
}
